import type { FC } from 'react';

import { MenuButton } from './menu-button';
import { MenuContent } from './menu-content';
import { MenuGroup } from './menu-group';
import { MenuHeading } from './menu-heading';
import { MenuItem } from './menu-item';
import { MenuItemIcon } from './menu-item-icon';
import { MenuItemLabel } from './menu-item-label';
import { MenuItemMeta } from './menu-item-meta';
import { MenuPortal } from './menu-portal';
import { MenuRoot } from './menu-root';
import { MenuTrigger } from './menu-trigger';
import type { MenuPropsInterface } from './types/menu.types';

/**
 * Renders as a disclosure navigation menu instead of a role="menu" component
 * so it can be used for a wider range of use cases.
 *
 * [ARIA Authoring Practices: Disclosure Navigation](https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/)
 *
 * @since 7.28.0
 */
export const Menu: FC<MenuPropsInterface> & {
  Root: typeof MenuRoot;
  Trigger: typeof MenuTrigger;
  Button: typeof MenuButton;
  Portal: typeof MenuPortal;
  Content: typeof MenuContent;
  Group: typeof MenuGroup;
  Heading: typeof MenuHeading;
  Item: typeof MenuItem;
  ItemIcon: typeof MenuItemIcon;
  ItemLabel: typeof MenuItemLabel;
  ItemMeta: typeof MenuItemMeta;
} = (props: MenuPropsInterface) => {
  const {
    children,
    content,
    trigger,
    css: cssProp,
    style,
    className,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    ...rest
  } = props;
  return (
    <Menu.Root {...rest}>
      <Menu.Trigger>{trigger}</Menu.Trigger>
      <Menu.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <Menu.Content css={cssProp} style={style} className={className}>
          {content || children}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

Menu.displayName = 'Menu';

Menu.Root = MenuRoot;
Menu.Trigger = MenuTrigger;
Menu.Button = MenuButton;
Menu.Portal = MenuPortal;
Menu.Content = MenuContent;
Menu.Group = MenuGroup;
Menu.Heading = MenuHeading;
Menu.Item = MenuItem;
Menu.ItemIcon = MenuItemIcon;
Menu.ItemLabel = MenuItemLabel;
Menu.ItemMeta = MenuItemMeta;
